import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { getColorValue, getTextColorFromToken } from '@/core/lib/color-contrast';
import type { Tag } from '../types';

interface TagChipProps {
  tag: Tag;
  onEdit: (tag: Tag) => void;
  onDelete: (tag: Tag) => void;
}

export function TagChip({ tag, onEdit, onDelete }: TagChipProps) {
  const { t } = useTranslation('tags');
  const bg = getColorValue(tag.color ?? '');
  const fg = getTextColorFromToken(tag.color ?? '');

  return (
    <div
      className="group inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium"
      style={{ backgroundColor: bg, color: fg }}
      data-testid="tag-chip"
    >
      <span data-testid="tag-chip-name">{tag.name}</span>

      <span className="inline-flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 rounded-full hover:bg-black/10"
              style={{ color: fg }}
              onClick={() => onEdit(tag)}
              data-testid="tag-chip-edit"
            >
              <Pencil className="h-3 w-3" />
              <span className="sr-only">{t('actions.edit')}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('actions.edit')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 rounded-full hover:bg-black/10"
              style={{ color: fg }}
              onClick={() => onDelete(tag)}
              data-testid="tag-chip-delete"
            >
              <Trash2 className="h-3 w-3" />
              <span className="sr-only">{t('actions.delete')}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('actions.delete')}</TooltipContent>
        </Tooltip>
      </span>
    </div>
  );
}
